// ───────────────────────── compare mode ─────────────────────────
import { useEffect, useRef, useState } from "react";
import type { RenderResult } from "../lib/protocol.ts";
import { blendRasters, diffRasters } from "../lib/raster.ts";

export type CompareRender = (pageIndex: number, scale: number) => Promise<RenderResult>;

/** Full-screen compare mode: the open document (A, left/red) against a
 *  picked PDF (B, right/cyan). A low-res sweep flags which pages
 *  differ; the current page renders full-res as side-by-side with red
 *  difference boxes, or as a red/cyan overlay. Read-only. */
export function CompareView({
  name,
  countA,
  countB,
  renderA,
  renderB,
  onClose,
}: {
  name: string;
  countA: number;
  countB: number;
  renderA: CompareRender;
  renderB: CompareRender;
  onClose: () => void;
}) {
  const pageCount = Math.max(countA, countB);
  const [current, setCurrent] = useState(0);
  const [mode, setMode] = useState<"side" | "overlay">("side");
  const [flags, setFlags] = useState<({ pct: number; differs: boolean } | null)[]>(() =>
    Array(pageCount).fill(null)
  );
  const [note, setNote] = useState<string | null>(null);
  const leftRef = useRef<HTMLCanvasElement>(null);
  const rightRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const jumpedRef = useRef(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setCurrent((c) => Math.min(pageCount - 1, c + 1));
      if (e.key === "ArrowLeft") setCurrent((c) => Math.max(0, c - 1));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, pageCount]);

  const grab = async (
    fn: CompareRender,
    page: number,
    count: number,
    scale: number
  ): Promise<{ width: number; height: number; data: Uint8ClampedArray }> => {
    if (page >= count) return { width: 0, height: 0, data: new Uint8ClampedArray(0) };
    const r = await fn(page, scale);
    return { width: r.width, height: r.height, data: new Uint8ClampedArray(r.pixels) };
  };

  // Low-res sweep: flag differing pages one by one; jump to the first
  // difference once it's known.
  useEffect(() => {
    let dead = false;
    void (async () => {
      for (let p = 0; p < pageCount; p += 1) {
        if (dead) return;
        try {
          const [a, b] = [
            await grab(renderA, p, countA, 0.5),
            await grab(renderB, p, countB, 0.5),
          ];
          const { changed, changedPx } = diffRasters(a, b);
          if (dead) return;
          // Absolute pixel floor: a one-character edit is a tiny
          // fraction of the page but still a real difference.
          const differs = changedPx >= 8;
          setFlags((f) => {
            const next = [...f];
            next[p] = { pct: changed, differs };
            return next;
          });
          if (!jumpedRef.current && differs) {
            jumpedRef.current = true;
            setCurrent(p);
          }
        } catch {
          if (!dead) {
            setFlags((f) => {
              const next = [...f];
              next[p] = { pct: 1, differs: true };
              return next;
            });
          }
        }
      }
    })();
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Full-res render of the current page in the active mode.
  useEffect(() => {
    let dead = false;
    void (async () => {
      try {
        const scale = 1.3;
        const [a, b] = [
          await grab(renderA, current, countA, scale),
          await grab(renderB, current, countB, scale),
        ];
        if (dead) return;
        setNote(
          current >= countA
            ? "This page only exists in the compared file."
            : current >= countB
              ? "This page only exists in the current document."
              : a.width !== b.width || a.height !== b.height
                ? "Page sizes differ — non-overlapping areas count as changes."
                : null
        );
        const { boxes } = diffRasters(a, b);
        const paint = (
          canvas: HTMLCanvasElement | null,
          img: { width: number; height: number; data: Uint8ClampedArray } | ImageData,
          withBoxes: boolean
        ) => {
          if (!canvas) return;
          const w = Math.max(1, img.width);
          const h = Math.max(1, img.height);
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d")!;
          ctx.fillStyle = "#fff";
          ctx.fillRect(0, 0, w, h);
          if (img.width > 0 && img.height > 0) {
            ctx.putImageData(
              img instanceof ImageData
                ? img
                : new ImageData(new Uint8ClampedArray(img.data), img.width, img.height),
              0,
              0
            );
          }
          if (withBoxes) {
            ctx.strokeStyle = "rgb(224 49 49 / 0.9)";
            ctx.lineWidth = 2;
            for (const bx of boxes) ctx.strokeRect(bx.x - 2, bx.y - 2, bx.w + 4, bx.h + 4);
          }
        };
        if (mode === "side") {
          paint(leftRef.current, a, true);
          paint(rightRef.current, b, true);
        } else {
          paint(overlayRef.current, blendRasters(a, b), false);
        }
      } catch {
        /* page render failed — leave the previous frame */
      }
    })();
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, mode, countA, countB]);

  const differing = flags.filter((f) => f?.differs).length;
  const swept = flags.filter((f) => f !== null).length;
  const canvasCls = "max-h-[calc(100vh-190px)] w-auto max-w-full bg-white shadow-md";
  const segCls = (active: boolean) =>
    `inline-flex h-7 items-center px-2.5 text-xs ${
      active
        ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
        : "hover:bg-[color:var(--color-bg-subtle)]"
    }`;

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-[color:var(--color-bg)]">
      <div className="flex flex-wrap items-center gap-2 border-b border-[color:var(--color-border)] px-3 py-2">
        <i className="ri-contrast-2-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
        <h2 className="text-sm font-semibold">Compare</h2>
        <span className="min-w-0 truncate text-xs text-[color:var(--color-fg-muted)]">
          current document ↔ {name}
        </span>
        <div className="ml-2 flex overflow-hidden rounded-md border border-[color:var(--color-border)]">
          <button type="button" onClick={() => setMode("side")} className={segCls(mode === "side")}>
            Side by side
          </button>
          <button type="button" onClick={() => setMode("overlay")} className={segCls(mode === "overlay")}>
            Overlay
          </button>
        </div>
        <div className="ml-auto flex items-center gap-1 text-xs">
          <button
            type="button"
            title="Previous page (←)"
            disabled={current === 0}
            onClick={() => setCurrent((c) => Math.max(0, c - 1))}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-30"
          >
            <i className="ri-arrow-left-s-line" aria-hidden />
          </button>
          <span>
            Page {current + 1} / {pageCount}
          </span>
          <button
            type="button"
            title="Next page (→)"
            disabled={current === pageCount - 1}
            onClick={() => setCurrent((c) => Math.min(pageCount - 1, c + 1))}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-30"
          >
            <i className="ri-arrow-right-s-line" aria-hidden />
          </button>
        </div>
        <span className="text-xs text-[color:var(--color-fg-muted)]">
          {swept < pageCount
            ? `scanning ${swept}/${pageCount}…`
            : differing === 0
              ? "no differences found"
              : differing === 1
                ? "1 page differs"
                : `${differing} pages differ`}
        </span>
        <button
          type="button"
          title="Close compare (Esc)"
          onClick={onClose}
          className="inline-flex h-7 items-center gap-1 rounded-md border border-[color:var(--color-border)] px-2.5 text-xs hover:bg-[color:var(--color-bg-subtle)]"
        >
          <i className="ri-close-line" aria-hidden /> Close
        </button>
      </div>

      {/* per-page diff strip */}
      <div className="flex flex-wrap items-center gap-1 border-b border-[color:var(--color-border)] px-3 py-1.5">
        {flags.map((f, i) => (
          <button
            key={i}
            type="button"
            title={
              f === null
                ? `Page ${i + 1} — scanning`
                : f.differs
                  ? `Page ${i + 1} — differs (${f.pct < 0.001 ? "<0.1" : (f.pct * 100).toFixed(1)}%)`
                  : `Page ${i + 1} — identical`
            }
            onClick={() => setCurrent(i)}
            className={`inline-flex h-6 min-w-6 items-center justify-center rounded px-1 text-[10px] ${
              i === current
                ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                : f === null
                  ? "text-[color:var(--color-fg-muted)]"
                  : f.differs
                    ? "bg-[#e03131]/15 text-[#e03131]"
                    : "text-[color:var(--color-fg-muted)] opacity-60"
            }`}
          >
            {i + 1}
          </button>
        ))}
      </div>

      {note && (
        <p className="border-b border-[color:var(--color-border)] bg-[color:var(--color-bg-subtle)] px-3 py-1 text-[11px] text-[color:var(--color-fg-muted)]">
          {note}
        </p>
      )}

      <div className="flex min-h-0 flex-1 items-start justify-center gap-4 overflow-auto bg-[color:var(--color-bg-subtle)] p-4">
        {mode === "side" ? (
          <>
            <figure className="text-center">
              <canvas ref={leftRef} className={canvasCls} />
              <figcaption className="mt-1 text-[10px] text-[color:var(--color-fg-muted)]">
                current document
              </figcaption>
            </figure>
            <figure className="text-center">
              <canvas ref={rightRef} className={canvasCls} />
              <figcaption className="mt-1 text-[10px] text-[color:var(--color-fg-muted)]">
                {name}
              </figcaption>
            </figure>
          </>
        ) : (
          <figure className="text-center">
            <canvas ref={overlayRef} className={canvasCls} />
            <figcaption className="mt-1 text-[10px] text-[color:var(--color-fg-muted)]">
              <span className="text-[#e03131]">red = only in the current document</span> ·{" "}
              <span className="text-[#0c8599]">cyan = only in {name}</span> · dark = unchanged
            </figcaption>
          </figure>
        )}
      </div>
    </div>
  );
}
